import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, signal, viewChild } from '@angular/core';
import { ClarityModule } from '@clr/angular';
import { ToastrService } from 'ngx-toastr';
import { Subject, firstValueFrom } from 'rxjs';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { errorHandle } from 'src/app/misc/functions';
import { Row } from 'src/app/models/ui/table';
import { GetByCallbackPipe } from 'src/app/pipes/get-by-callback.pipe';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { LeftPadPipe } from 'src/app/pipes/left-pad.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { ConfirmService } from 'src/app/services/confirm.service';
import { OverlayService } from 'src/app/services/Overlay/overlay.service';
import { PreviewThumbnailsComponent } from '../../_shared/preview-thumbnails/preview-thumbnails.component';
import { SignaturePadModalComponent } from '../../_shared/signature-pad-modal/signature-pad-modal.component';
import { AbnahmeProtokollStore } from './abnahme-protokoll.store';
import { AbnahmeMangel, AbnahmeOverlayInput, AbnahmeSaveRequest, AbnahmeSignatures } from './abnahme-protokoll.types';
import { Key } from 'src/app/types/shared';

@Component({
  selector: 'app-abnahme-protokoll',
  templateUrl: './abnahme-protokoll.component.html',
  styleUrls: ['./abnahme-protokoll.component.scss'],
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    PreviewThumbnailsComponent,
    SignaturePadModalComponent,
    MrTranslatePipe,
    LeftPadPipe,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
    BackendLocaleDatePipe,
    GetByCallbackPipe,
  ],
})
export class AbnahmeProtokollComponent implements OnDestroy {
  private readonly overlayService = inject(OverlayService);
  private readonly apiService = inject(APIService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly toastr = inject(ToastrService);
  private readonly confirmService = inject(ConfirmService);
  protected readonly store = inject(AbnahmeProtokollStore);
  protected readonly localeDate = inject(BackendLocaleDatePipe);

  private readonly preview = viewChild(PreviewThumbnailsComponent);

  public readonly closedialog = new Subject<boolean>();

  protected titel: string;
  protected auftragnehmer: string;

  protected readonly geprueftChecks = GEPRUEFT;
  protected readonly genommenChecks = GENOMMEN;

  protected readonly isZoomedIn = signal(true);
  protected readonly openSignPad = signal<Key<AbnahmeSignatures> | null>(null);

  private dataRefresh: Subject<void>;
  private refreshAbnahmen: Subject<boolean>;

  protected getSelectedObjektIcon = (objekt: Row | null) => (objekt ? `objekttyp${objekt['typ']}` : undefined);
  protected protokollHighlight = (fileName: string = '') => /\$Abnahme-Protokoll.*\.pdf$/i.test(fileName);
  protected getLargeFile = (id: number | string) => firstValueFrom(this.apiService.getAbnahmeLargeFile(Number(id)));

  public set data({ data: inData, dataRefresh, refreshAbnahmen }: AbnahmeOverlayInput) {
    if (!inData.objTable?.rows.length || !inData.firmenId) {
      this.toastr.warning(
        this.mrTranslate.transform('Objekte sind leer oder Firma wurde entfernt'),
        this.mrTranslate.transform('Auftrag ist ungültig')
      );
      this.closedialog.next(true);
      return;
    }

    this.dataRefresh = dataRefresh;
    this.refreshAbnahmen = refreshAbnahmen;

    this.titel = inData.titel || 'Instandsetzung';
    this.auftragnehmer = inData.auftragnehmer || 'Firma';

    const session = this.store.beginSession(inData.id, inData.firmenId, inData.objTable.rows, inData.abnahmeId);
    if (session.resumed) {
      setTimeout(() => (this.preview().rawPreviewThumbnails = session.previews));
    } else if (inData.abnahmeId != null) {
      this.loadAbnahmeDetails(inData.abnahmeId);
    }
  }

  private async loadAbnahmeDetails(abnahmeId: number) {
    try {
      const { abnahme, maengel, files } = await firstValueFrom(this.apiService.getAbnahme(abnahmeId));

      this.store.setMaengel(maengel);
      this.store.loadAbnahme(abnahme);

      setTimeout(() => {
        this.preview().unpackFiles(files, previews => {
          this.store.setPreviewThumbnails(previews);
        });
      });
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Abnahme konnte nicht geladen werden'));
      this.close();
    }
  }

  protected async submit() {
    if (this.store.form.invalid) {
      this.store.form.markAllAsTouched();
      return;
    }

    await this.saveAbnahmeProtokoll();
  }

  protected async saveAbnahmeProtokoll() {
    const contentChanged = this.hasContentChanges();
    const filesChanged = this.hasFileChanges();

    if (!contentChanged && !filesChanged) {
      this.toastr.info(this.mrTranslate.transform('Nichts wurde verändert'));
      return;
    }

    try {
      if (contentChanged) {
        await this.saveContent();
      }

      if (filesChanged) {
        await this.saveFiles();
      }

      this.dataRefresh.next();
      this.refreshAbnahmen.next(true);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Beim Speichern ist ein Fehler aufgetreten!'));
    }
  }

  private hasContentChanges(): boolean {
    return (
      this.store.form.dirty ||
      this.store.signaturesChanged() ||
      this.store.maengelChanged() ||
      this.store.legacyValuesChanged()
    );
  }
  private hasFileChanges(): boolean {
    return this.store.deletedFiles().length > 0 || this.preview().unsaved().length > 0;
  }

  private getSaveRequest(): AbnahmeSaveRequest {
    const auftragId = this.store.auftragId();

    if (auftragId == null) {
      throw new Error('Eine AuftragId ist erforderlich.');
    }

    return {
      abnahmeId: this.store.abnahmeId() ?? 0,
      language: localStorage.getItem('language') || 'de',
      formValues: {
        ...this.store.form.getRawValue(),
        AuftragId: auftragId,
        FirmaID: this.store.firmenId(),
        MangelIds: this.store.maengel().rows.map(row => row.OMAZSID),
        ObjektIds: this.store.objekts().map(objekt => objekt['ostammid'] as number),
        Signatures: this.store.signatures(),
      },
    };
  }

  private async saveContent(): Promise<void> {
    const { abnahmeId, pdf } = await firstValueFrom(this.apiService.saveAbnahme(this.getSaveRequest()));

    this.store.updateAbnahme(abnahmeId);
    this.store.markContentSaved();

    const preview = this.preview();
    const existing = preview.rawPreviewThumbnails.filter(thumbnail => !thumbnail.highlight);
    preview.unpackFiles([pdf], pdfPreviews => (preview.rawPreviewThumbnails = [...existing, ...pdfPreviews]));

    this.toastr.success(
      this.mrTranslate.transform('PDF Dokument wurde erstellt'),
      this.mrTranslate.transform('Abnahme ist gespeichert')
    );
  }

  private async saveFiles(): Promise<void> {
    const id = this.store.abnahmeId();
    if (id == null) {
      throw new Error('Eine AbnahmeId ist erforderlich.');
    }

    const preview = this.preview();
    const unsaved = preview.unsaved();
    const files = await preview.packUnsavedFiles();

    const { files: savedFiles } = await firstValueFrom(
      this.apiService.saveAbnahmeFiles(id, {
        files,
        deletedFiles: this.store.deletedFiles(),
      })
    );

    this.store.setDeletedFiles([]);
    preview.setSaved();
    this.toastr.success(this.mrTranslate.transform('Die Dateien sind gespeichert'));

    if (savedFiles.length !== unsaved.length) {
      this.toastr.warning(
        this.mrTranslate.transform(
          'Nicht alle Dateien konnten zugeordnet werden. Löschen ist nur nach erneutem Öffnen möglich'
        )
      );
      return;
    }

    savedFiles.forEach((saved, index) => {
      const thumbnail = unsaved[index];
      if (!thumbnail) return;

      thumbnail.id = saved.id;
      thumbnail.name = saved.name;
    });
  }

  protected addNeuMangel() {
    const objekt = this.store.selectedObjekt();
    if (!objekt) return;

    this.overlayService.setOverlay({
      overlay: 'mangelerfassung',
      isTopOverlay: true,
      id: {
        ostammid: objekt['ostammid'],
        otypid: objekt['otypid'],
        typ_Line_Point: objekt['typ_id'],
        startmeter: objekt['StartMeter'],
        endmeter: objekt['EndMeter'],
      },
    });
  }

  protected async deleteMangel(row: AbnahmeMangel) {
    try {
      await this.apiService.deleteMangel(row.OMAZSID);
      this.store.removeMangel(row.OMAZSID);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Mangel könnte nicht gelöscht werden'));
    }
  }

  protected deleteDoc(id: number | string) {
    const fileId = Number(id);
    if (Number.isInteger(fileId) && fileId > 0) {
      this.store.addDeletedFile(fileId);
    }
  }

  protected openPdf(file: File) {
    this.overlayService.setOverlay({
      overlay: 'viewAuftrag',
      isTopOverlay: true,
      data: {
        data: file,
        titel: file?.name || this.mrTranslate.transform('Datei'),
        file: file?.name || `${this.store.abnahmeId()}.pdf`,
      },
    });
  }

  protected sign(imgUrl: string) {
    this.store.setSignature(this.openSignPad(), imgUrl);
    this.openSignPad.set(null);
  }

  protected async close() {
    if (this.store.maengelChanged()) {
      await this.confirmService.warning(
        this.mrTranslate.transform('Die Mängeländerungen müssen zuerst gespeichert werden.'),
        false
      );

      return;
    }

    if (!this.store.isNew() && this.hasFileChanges()) {
      const saveFiles = await this.confirmService.warning(
        this.mrTranslate.transform(
          'Es gibt noch nicht gespeicherte Änderungen an den Dateien. Möchten Sie diese speichern?'
        ),
        true,
        'Speichern',
        'Verwerfen'
      );

      if (saveFiles) {
        try {
          await this.saveFiles();
          this.dataRefresh.next();
        } catch (er) {
          this.toastr.error(
            errorHandle(er),
            this.mrTranslate.transform('Beim Speichern von Bilder und Video ist ein Fehler aufgetreten')
          );
          return;
        }
      }
    }

    this.store.clear();
    this.closedialog.next(true);
  }

  ngOnDestroy() {
    if (this.store.auftragId() != null) {
      this.store.setPreviewThumbnails([...this.preview().rawPreviewThumbnails]);
    }
  }
}

const GEPRUEFT = [
  { field: 'CHK1', label: 'Spurweite' },
  { field: 'CHK2', label: 'Gleislage' },
  { field: 'CHK3', label: 'Schwellenteilung / Lage' },
  { field: 'CHK4', label: 'Bettung' },
  { field: 'CHK5', label: 'Schienenverspannung' },
  { field: 'CHK6', label: 'Verschlußteile' },
  { field: 'CHK7', label: 'Randwege' },
  { field: 'CHK8', label: 'Freimeldung' },
] as const;

const GENOMMEN = [
  { field: 'FUNC1', label: 'abgenommen' },
  { field: 'FUNC2', label: 'in Betrieb genommen' },
  { field: 'FUNC3', label: 'per Funk freigegeben' },
] as const;
